import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import {
  Box, Typography, Button, Paper, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Dialog, DialogTitle,
  DialogContent, DialogActions, TextField, Grid, Chip, MenuItem,
  IconButton, Tooltip, Alert, CircularProgress, Autocomplete, Divider,
  Collapse, FormControlLabel, Checkbox, InputAdornment
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import BlockIcon from '@mui/icons-material/Block';
import VisibilityIcon from '@mui/icons-material/Visibility';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import PersonSearchIcon from '@mui/icons-material/PersonSearch';
import SearchIcon from '@mui/icons-material/Search';
import LinkIcon from '@mui/icons-material/Link';

const TIPOS_DOC_SUNAT = [
  { code: '01', label: '01 — Factura' },
  { code: '02', label: '02 — Recibo por Honorarios' },
  { code: '03', label: '03 — Boleta de Venta' },
  { code: '04', label: '04 — Liquidación de Compra' },
  { code: '07', label: '07 — Nota de Crédito' },
  { code: '08', label: '08 — Nota de Débito' },
  { code: '12', label: '12 — Ticket (Máquina registradora)' },
  { code: '14', label: '14 — Recibo Serv. Públicos' },
  { code: '18', label: '18 — Documento Bancario' },
  { code: '23', label: '23 — Póliza de Adjudicación' },
  { code: '50', label: '50 — Declaración de Aduanas (DUA)' },
  { code: '52', label: '52 — Despacho Simplificado (DAM)' },
  { code: '91', label: '91 — No Domiciliado' },
  { code: '96', label: '96 — Exceso Crédito Fiscal' },
];

// Etiqueta corta (sin el código numérico repetido) para mostrar en el listado de Asientos.
const DOC_SUNAT_CORTO = Object.fromEntries(
  TIPOS_DOC_SUNAT.map(t => [t.code, t.label.split('—')[1]?.trim() || t.label])
);

const MANUALES_TIPOS = ['DI', 'AJ', 'AP', 'CI', 'DP'];
const CAJA_BANCOS_TIPOS = ['RE', 'BP', 'CH', 'TF', 'DE', 'NA', 'CB'];

const TIPOS_LIBRO = [
  { code: '01', label: '01 — Diario General' },
  { code: '02', label: '02 — Registro de Ventas' },
  { code: '03', label: '03 — Registro de Compras' },
  { code: '06', label: '06 — Caja y Bancos' },
  { code: '09', label: '09 — Honorarios / Retenciones' },
  { code: '12', label: '12 — Activo Fijo' },
];

// Solo para el FILTRO de la pantalla de listado (no es un tipo_libro real adicional —
// el PLE sigue usando '06' para ambos). Separa "06 — Caja y Bancos" en dos opciones según
// la cuenta de caja/banco de la línea: 101x = Caja Efectivo, cualquier otra cuenta del
// elemento 10 (104 bancos, 106 depósitos, etc.) = Caja y Bancos — para que las
// cancelaciones de cobro/pago se puedan ubicar más rápido en la pantalla.
const TIPOS_LIBRO_FILTRO = [
  { code: '01', label: '01 — Diario General' },
  { code: '02', label: '02 — Registro de Ventas' },
  { code: '03', label: '03 — Registro de Compras' },
  { code: '06:EFECTIVO', label: '06 — Caja Efectivo (101)' },
  { code: '06:BANCOS', label: '06 — Caja y Bancos (demás ctas. 10)' },
  { code: '09', label: '09 — Honorarios / Retenciones' },
  { code: '12', label: '12 — Activo Fijo' },
  // No son tipo_libro PLE — filtran por tipo_documento ('CA'/'AP'), que es como el
  // módulo Cierre Contable marca los asientos de cierre anual y de apertura del
  // ejercicio siguiente. Se exponen aquí para que también se puedan ubicar desde la
  // pantalla general de Asientos, sin tener que entrar a Cierre Contable.
  { code: 'DOC:CA', label: 'Cierre Anual' },
  { code: 'DOC:AP', label: 'Apertura' },
];

const EMPTY_LINEA = { cuenta_id: '', cuentaObj: null, glosa_det: '', debe: '', haber: '', auxiliar_ref: '', auxiliar_id: null, auxiliar_nombre: '', referencia: '', urc_codigo: '', centro_costo_id: '' };
const EMPTY_PLE = {
  tipo_libro: '', doc_tipo_sunat: '', doc_serie: '', doc_numero: '',
  doc_fecha_vencimiento: '', doc_ref_tipo: '', doc_ref_serie: '', doc_ref_numero: '',
  igv_especial: false, gasto_no_deducible: false,
  tiene_detraccion: false, det_fecha: '', det_numero: '', det_tasa: '', det_monto: '', det_tipo: '',
  tiene_retencion: false, ret_codigo_tasa: '', ret_numero: '',
  tiene_percepcion: false, per_codigo_tasa: '', per_numero: '',
  aduana_codigo: '', proyecto_nombre: ''
};
const EMPTY_FORM = { tipo_documento: '', fecha: '', glosa: '', moneda: 'PEN', tipo_tc: 'LIBRE', tipo_cambio: '1.00' };

export default function Asientos({ abrirAsientoId, onAsientoAbierto } = {}) {
  const [empresas, setEmpresas] = useState([]);
  const [empresaId, setEmpresaId] = useState('');
  const [periodos, setPeriodos] = useState([]);
  const [periodoSel, setPeriodoSel] = useState('');
  const [tiposDoc, setTiposDoc] = useState([]);
  const [filtroTipoDoc, setFiltroTipoDoc] = useState('');
  const [filtroTipoLibro, setFiltroTipoLibro] = useState('');
  const [asientos, setAsientos] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [verAsiento, setVerAsiento] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [lineas, setLineas] = useState([{ ...EMPTY_LINEA }, { ...EMPTY_LINEA }]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [sugerencia, setSugerencia] = useState(null); // { cuentaObj, reglas, destino }
  const [ple, setPle] = useState(EMPTY_PLE);
  const [pleOpen, setPleOpen] = useState(false);
  const [editandoId, setEditandoId] = useState(null);
  const [confirmEliminar, setConfirmEliminar] = useState(null); // { id, ref }
  const [urcs, setUrcs] = useState([]);
  const [centrosCosto, setCentrosCosto] = useState([]);

  // Cargar catálogo URC una vez
  useEffect(() => {
    axios.get('/api/contabilidad/urc')
      .then(r => setUrcs(Array.isArray(r.data) ? r.data : []))
      .catch(() => {});
  }, []);

  // Auto-fetch tipo de cambio SBS cuando fecha/tipo_tc cambian — 2 pasos (tabla interna de
  // Contabilidad primero, API de SUNAT en vivo como respaldo si esa fecha no está cargada
  // todavía, ej. una fecha antigua de un asiento de apertura), mismo patrón de Registro de
  // Compras/Ventas. El campo queda editable igual, por si el contador necesita corregirlo.
  useEffect(() => {
    if (!form.fecha || form.moneda === 'PEN' || !['COMPRA_SBS', 'VENTA_SBS'].includes(form.tipo_tc)) return;
    let cancelled = false;
    axios.get('/api/contabilidad/tipo-cambio', { params: { fecha: form.fecha } })
      .then(r => {
        if (cancelled) return;
        const tc = form.tipo_tc === 'COMPRA_SBS' ? r.data.compra : r.data.venta;
        if (tc) { setForm(f => ({ ...f, tipo_cambio: String(parseFloat(tc).toFixed(3)) })); return; }
        throw new Error('sin dato');
      })
      .catch(() => {
        if (cancelled) return;
        axios.get('/api/tipo-cambio/sunat', { params: { fecha: form.fecha } })
          .then(r => {
            if (cancelled || !r.data) return;
            const tc = form.tipo_tc === 'COMPRA_SBS' ? r.data.compra : r.data.venta;
            if (tc) setForm(f => ({ ...f, tipo_cambio: String(parseFloat(tc).toFixed(3)) }));
          })
          .catch(() => {});
      });
    return () => { cancelled = true; };
  }, [form.fecha, form.tipo_tc, form.moneda]);

  const handleCuentaSelected = useCallback(async (cuenta, lineaIdx) => {
    if (!cuenta || !empresaId) return;
    setSugerencia(null);
    if (cuenta.urc_codigo_default) {
      setLineas(prev => prev.map((l, idx) => idx === lineaIdx ? { ...l, urc_codigo: cuenta.urc_codigo_default } : l));
    }
    // Auto-fill referencia en cuentas 12x / 42x según tipo de asiento
    const cod = cuenta.codigo || '';
    if (cod.startsWith('12') || cod.startsWith('42')) {
      setLineas(prev => prev.map((l, idx) => {
        if (idx !== lineaIdx || l.referencia) return l;
        let autoRef = '';
        if (form.tipo_documento === 'NC' || form.tipo_documento === 'ND') {
          autoRef = ple?.doc_ref_serie && ple?.doc_ref_numero
            ? `${ple.doc_ref_serie}-${ple.doc_ref_numero}` : '';
        } else if (['VE','CO'].includes(form.tipo_documento)) {
          autoRef = ple?.doc_serie && ple?.doc_numero
            ? `${ple.doc_serie}-${ple.doc_numero}` : '';
        } else if (CAJA_BANCOS_TIPOS.includes(form.tipo_documento)) {
          autoRef = ple?.doc_ref_serie && ple?.doc_ref_numero
            ? `${ple.doc_ref_serie}-${ple.doc_ref_numero}` : '';
        }
        return autoRef ? { ...l, referencia: autoRef } : l;
      }));
    }
    try {
      const r = await axios.get('/api/contabilidad/reglas-rapido/cuenta', {
        params: { empresa_id: empresaId, codigo: cuenta.codigo, moneda: form.moneda || 'PEN', tipo_documento: form.tipo_documento || undefined }
      });
      const reglas = Array.isArray(r.data) ? r.data : [];
      const destino = cuenta.cuenta_destino_id ? {
        id: cuenta.cuenta_destino_id,
        codigo: cuenta.cuenta_destino_codigo,
        descripcion: cuenta.cuenta_destino_descripcion
      } : null;
      if (reglas.length > 0 || destino) {
        setSugerencia({ cuentaObj: cuenta, lineaIdx, reglas, destino });
      }
    } catch (_) {}
  }, [empresaId, form.tipo_documento, ple]);

  useEffect(() => {
    axios.get('/api/contabilidad/empresas')
      .then(r => {
        const todas = Array.isArray(r.data) ? r.data : [];
        setEmpresas(todas);
        const activas = todas.filter(e => e.activo);
        if (activas.length === 1) setEmpresaId(String(activas[0].id));
      }).catch(() => {});
  }, []);

  useEffect(() => {
    if (!empresaId) { setPeriodos([]); setPeriodoSel(''); setTiposDoc([]); return; }

    axios.get('/api/contabilidad/periodos', { params: { empresa_id: empresaId } })
      .then(r => {
        const todos = Array.isArray(r.data) ? r.data : [];
        setPeriodos(todos);
        const hoy = new Date();
        const mes = String(hoy.getMonth() + 1).padStart(2, '0');
        const clave = `${hoy.getFullYear()}${mes}`;
        const actual = todos.find(p => p.periodo === clave);
        if (actual) setPeriodoSel(actual.periodo);
        else if (todos.length > 0) setPeriodoSel(todos[todos.length - 1].periodo);
        else setPeriodoSel('');
      }).catch(() => {});

    axios.get('/api/contabilidad/tipos-documento', { params: { empresa_id: empresaId } })
      .then(r => setTiposDoc(Array.isArray(r.data) ? r.data : []))
      .catch(() => {});

    axios.get('/api/contabilidad/centros-costo', { params: { empresa_id: empresaId } })
      .then(r => setCentrosCosto(Array.isArray(r.data) ? r.data.filter(c => c.activo) : []))
      .catch(() => {});
  }, [empresaId]);

  const empresaSel = empresas.find(e => String(e.id) === String(empresaId));
  const usaCentroCosto = !!empresaSel?.usa_centro_costo;

  const cargar = useCallback(async () => {
    if (!empresaId) return;
    setLoading(true);
    try {
      const params = { empresa_id: empresaId };
      if (periodoSel)       params.periodo        = periodoSel;
      if (filtroTipoDoc)    params.tipo_documento = filtroTipoDoc;
      if (filtroTipoLibro) {
        if (filtroTipoLibro.startsWith('DOC:')) {
          params.tipo_documento = filtroTipoLibro.slice(4);
        } else {
          const [tipoLibroReal, cajaTipo] = filtroTipoLibro.split(':');
          params.tipo_libro = tipoLibroReal;
          if (cajaTipo) params.caja_tipo = cajaTipo;
        }
      }
      const r = await axios.get('/api/contabilidad/asientos', { params });
      setAsientos(r.data.data || []);
      setTotal(r.data.total || 0);
    } catch {}
    setLoading(false);
  }, [empresaId, periodoSel, filtroTipoDoc, filtroTipoLibro]);

  useEffect(() => { cargar(); }, [cargar]);

  const nuevaLinea = () => setLineas(prev => [...prev, { ...EMPTY_LINEA }]);
  const quitarLinea = i => setLineas(prev => prev.filter((_, idx) => idx !== i));
  const updateLinea = (i, field, value) =>
    setLineas(prev => prev.map((l, idx) => idx === i ? { ...l, [field]: value } : l));

  const totalDebe = lineas.reduce((s, l) => s + (parseFloat(l.debe) || 0), 0);
  const totalHaber = lineas.reduce((s, l) => s + (parseFloat(l.haber) || 0), 0);
  const diferencia = Math.abs(totalDebe - totalHaber);
  const cuadrado = diferencia < 0.01 && totalDebe > 0;

  const periodoActual = periodos.find(p => p.periodo === periodoSel);
  const periodoAbierto = periodoActual?.estado === 'ABIERTO';

  const abrirNuevo = () => {
    const hoy = new Date().toLocaleDateString('en-CA');
    setForm({ ...EMPTY_FORM, fecha: hoy });
    setLineas([{ ...EMPTY_LINEA }, { ...EMPTY_LINEA }]);
    setSugerencia(null);
    setPle(EMPTY_PLE);
    setPleOpen(false);
    setEditandoId(null);
    setError('');
    setOpen(true);
  };

  const abrirEditar = async (asiento) => {
    try {
      const r = await axios.get(`/api/contabilidad/asientos/${asiento.id}`);
      const a = r.data;
      if (a.empresa_id) setEmpresaId(String(a.empresa_id));
      setForm({
        tipo_documento: a.tipo_documento || '',
        fecha: a.fecha?.slice(0, 10) || '',
        glosa: a.glosa || '',
        moneda: a.moneda || 'PEN',
        tipo_tc: 'LIBRE',
        tipo_cambio: String(parseFloat(a.tipo_cambio || 1).toFixed(3))
      });
      const lineasCargadas = (a.detalles || []).map(d => ({
        cuenta_id: d.cuenta_id || '',
        cuentaObj: { id: d.cuenta_id, codigo: d.cuenta_codigo, descripcion: d.cuenta_descripcion, urc_codigo_default: d.urc_codigo_default, requiere_auxiliar: d.requiere_auxiliar },
        glosa_det: d.glosa_det || '',
        debe: d.debe ? String(parseFloat(d.debe).toFixed(2)) : '',
        haber: d.haber ? String(parseFloat(d.haber).toFixed(2)) : '',
        auxiliar_id: d.auxiliar_id || null,
        auxiliar_nombre: d.auxiliar_nombre || '',
        auxiliar_ref: d.auxiliar_nombre || '',
        referencia: d.referencia || '',
        urc_codigo: d.urc_codigo || '',
        centro_costo_id: d.centro_costo_id || ''
      }));
      if (lineasCargadas.length < 2) lineasCargadas.push({ ...EMPTY_LINEA });
      setLineas(lineasCargadas);
      setPle({
        tipo_libro: a.tipo_libro || '',
        doc_tipo_sunat: a.doc_tipo_sunat || '',
        doc_serie: a.doc_serie || '',
        doc_numero: a.doc_numero || '',
        doc_fecha_vencimiento: a.doc_fecha_vencimiento?.slice(0, 10) || '',
        doc_ref_tipo: a.doc_ref_tipo || '',
        doc_ref_serie: a.doc_ref_serie || '',
        doc_ref_numero: a.doc_ref_numero || '',
        igv_especial: !!a.igv_especial,
        gasto_no_deducible: !!a.gasto_no_deducible,
        tiene_detraccion: !!a.tiene_detraccion,
        det_fecha: a.det_fecha?.slice(0, 10) || '',
        det_numero: a.det_numero || '',
        det_tasa: a.det_tasa || '',
        det_monto: a.det_monto || '',
        det_tipo: a.det_tipo || '',
        tiene_retencion: !!a.tiene_retencion,
        ret_codigo_tasa: a.ret_codigo_tasa || '',
        ret_numero: a.ret_numero || '',
        tiene_percepcion: !!a.tiene_percepcion,
        per_codigo_tasa: a.per_codigo_tasa || '',
        per_numero: a.per_numero || '',
        aduana_codigo: a.aduana_codigo || '',
        proyecto_nombre: a.proyecto_nombre || ''
      });
      setPleOpen(false);
      setSugerencia(null);
      setEditandoId(asiento.id);
      setError('');
      setOpen(true);
    } catch (e) {
      setError(e.response?.data?.error || 'Error al cargar asiento');
    }
  };

  // Deep-link: otra pantalla (ej. Validador de Cuadre en Reportes Contables) pidió abrir
  // un asiento específico apenas se monta esta página.
  useEffect(() => {
    if (!abrirAsientoId) return;
    abrirEditar({ id: abrirAsientoId }).finally(() => onAsientoAbierto && onAsientoAbierto());
  }, [abrirAsientoId]);

  const eliminarAsiento = async () => {
    if (!confirmEliminar) return;
    try {
      await axios.delete(`/api/contabilidad/asientos/${confirmEliminar.id}`);
      setSuccess('Asiento eliminado correctamente');
      setConfirmEliminar(null);
      if (verAsiento?.id === confirmEliminar.id) setVerAsiento(null);
      cargar();
    } catch (e) {
      setError(e.response?.data?.error || e.message);
      setConfirmEliminar(null);
    }
  };

  const aplicarSugerencia = () => {
    if (!sugerencia) return;
    const nuevasLineas = [];
    const monto = parseFloat(lineas[sugerencia.lineaIdx]?.debe || lineas[sugerencia.lineaIdx]?.haber || 0);
    for (const r of sugerencia.reglas) {
      const val = monto > 0 ? ((monto * r.porcentaje) / 100).toFixed(2) : '';
      nuevasLineas.push({
        ...EMPTY_LINEA,
        cuenta_id: r.cuenta_id,
        cuentaObj: { id: r.cuenta_id, codigo: r.cuenta_codigo, descripcion: r.cuenta_descripcion },
        glosa_det: r.descripcion_linea || '',
        debe: r.lado === 'D' ? val : '',
        haber: r.lado === 'H' ? val : '',
        urc_codigo: r.urc_codigo_default || ''
      });
    }
    if (sugerencia.destino) {
      nuevasLineas.push({ ...EMPTY_LINEA, cuenta_id: sugerencia.destino.id,
        cuentaObj: sugerencia.destino, glosa_det: 'Destino costos',
        urc_codigo: sugerencia.destino.urc_codigo_default || '' });
    }
    // Limpiar filas vacías (sin cuenta y sin montos) antes de agregar las automáticas
    setLineas(prev => {
      const sinVacias = prev.filter(l =>
        l.cuenta_id || parseFloat(l.debe) > 0 || parseFloat(l.haber) > 0
      );
      return [...sinVacias, ...nuevasLineas];
    });
    setSugerencia(null);
  };

  const guardar = async () => {
    if (!form.tipo_documento) { setError('Seleccione el tipo de documento contable'); return; }
    if (!form.fecha) { setError('Ingrese la fecha del asiento'); return; }
    if (!form.glosa.trim()) { setError('Ingrese una glosa descriptiva'); return; }
    const detalles = lineas
      .filter(l => l.cuenta_id && (parseFloat(l.debe) > 0 || parseFloat(l.haber) > 0))
      .map(l => ({
        cuenta_id: l.cuenta_id,
        glosa_det: l.glosa_det,
        debe: parseFloat(l.debe) || 0,
        haber: parseFloat(l.haber) || 0,
        auxiliar_ref: l.auxiliar_ref || null,
        auxiliar_id: l.auxiliar_id || null,
        auxiliar_nombre: l.auxiliar_nombre || null,
        referencia: l.referencia || null,
        urc_codigo: l.urc_codigo || null,
        centro_costo_id: l.centro_costo_id || null
      }));
    if (detalles.length < 2) { setError('El asiento debe tener al menos 2 líneas con cuenta y monto'); return; }
    if (!cuadrado) { setError(`El asiento no cuadra. Diferencia: ${diferencia.toFixed(2)}`); return; }

    setSaving(true);
    setError('');
    const payload = {
      empresa_id: empresaId,
      periodo: periodoSel,
      tipo_documento: form.tipo_documento,
      fecha: form.fecha,
      glosa: form.glosa,
      moneda: form.moneda,
      tipo_cambio: parseFloat(form.tipo_cambio) || 1,
      detalles,
      ...ple,
      det_tasa: ple.det_tasa ? parseFloat(ple.det_tasa) : null,
      det_monto: ple.det_monto ? parseFloat(ple.det_monto) : null
    };
    try {
      if (editandoId) {
        await axios.put(`/api/contabilidad/asientos/${editandoId}`, payload);
        setSuccess('Asiento actualizado correctamente');
      } else {
        const r = await axios.post('/api/contabilidad/asientos', payload);
        setSuccess(`Asiento ${form.tipo_documento}-${r.data.numero} registrado correctamente`);
      }
      setOpen(false);
      cargar();
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    }
    setSaving(false);
  };

  const anular = async (id, ref) => {
    if (!window.confirm(`¿Anular el asiento ${ref}? Esta acción no se puede deshacer.`)) return;
    try {
      await axios.put(`/api/contabilidad/asientos/${id}/anular`);
      setSuccess('Asiento anulado correctamente');
      cargar();
      if (verAsiento?.id === id) setVerAsiento(null);
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    }
  };

  const verDetalle = async (a) => {
    try {
      const r = await axios.get(`/api/contabilidad/asientos/${a.id}`);
      setVerAsiento(r.data);
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    }
  };

  const fmtNum = v => parseFloat(v || 0).toFixed(2);

  return (
    <Box sx={{ p: { xs: 1, sm: 2, md: 3 }, width: '100%' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, gap: 2, flexWrap: 'wrap' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexShrink: 0 }}>
          <ReceiptLongIcon color="primary" sx={{ fontSize: 32 }} />
          <Typography variant="h5" fontWeight="bold">Asientos Contables</Typography>
        </Box>
        <TextField select label="Empresa" size="small" value={empresaId}
          onChange={e => setEmpresaId(e.target.value)}
          sx={{ minWidth: { xs: '100%', sm: 240 }, ml: { sm: 'auto' } }}>
          {empresas.map(e => (
            <MenuItem key={e.id} value={String(e.id)}>
              {e.razon_social}{!e.activo && <span style={{ color: '#999', marginLeft: 6 }}>(Inactiva)</span>}
            </MenuItem>
          ))}
        </TextField>
        <TextField select label="Período" size="small" value={periodoSel}
          onChange={e => setPeriodoSel(e.target.value)} sx={{ minWidth: 180 }}
          disabled={periodos.length === 0}>
          {periodos.sort((a, b) => b.periodo.localeCompare(a.periodo)).map(p => (
            <MenuItem key={p.periodo} value={p.periodo}>
              {p.nombre || p.periodo}
              {p.estado !== 'ABIERTO' && (
                <span style={{ color: '#999', marginLeft: 6, fontSize: 11 }}>({p.estado})</span>
              )}
            </MenuItem>
          ))}
        </TextField>
        <TextField select label="Tipo Libro" size="small" value={filtroTipoLibro}
          onChange={e => setFiltroTipoLibro(e.target.value)} sx={{ minWidth: 170 }}>
          <MenuItem value="">— Todos los libros —</MenuItem>
          {TIPOS_LIBRO_FILTRO.map(t => <MenuItem key={t.code} value={t.code}>{t.label}</MenuItem>)}
        </TextField>
        <TextField select label="Tipo Documento" size="small" value={filtroTipoDoc}
          onChange={e => setFiltroTipoDoc(e.target.value)} sx={{ minWidth: 180 }}>
          <MenuItem value="">— Todos los tipos —</MenuItem>
          {tiposDoc.map(t => <MenuItem key={t.codigo} value={t.codigo}>{t.codigo} — {t.descripcion}</MenuItem>)}
        </TextField>
        {empresaId && periodoAbierto && (
          <Button variant="contained" startIcon={<AddIcon />} onClick={abrirNuevo}>
            Nuevo Asiento
          </Button>
        )}
      </Box>

      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}
      {error && !open && !verAsiento && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      {!empresaId ? (
        <Alert severity="info">Seleccione una empresa contable para ver sus asientos.</Alert>
      ) : !periodoSel ? (
        <Alert severity="warning">No hay períodos registrados. Cree períodos en "Períodos Contables" primero.</Alert>
      ) : (
        <>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
            <Typography variant="body2" color="text.secondary">
              {total} asiento(s) en período {periodoSel}
            </Typography>
            {periodoActual && (
              <Chip
                label={periodoActual.estado}
                color={periodoActual.estado === 'ABIERTO' ? 'success' : 'error'}
                size="small"
              />
            )}
          </Box>
          <TableContainer component={Paper} elevation={2} sx={{ overflowX: 'auto' }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: 'primary.main' }}>
                    <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>N°</TableCell>
                    <TableCell className="cont-col-sm" sx={{ color: 'white', fontWeight: 'bold' }}>Tipo</TableCell>
                    <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Fecha</TableCell>
                    <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Glosa</TableCell>
                    <TableCell className="cont-col-sm" sx={{ color: 'white', fontWeight: 'bold' }} align="right">Debe</TableCell>
                    <TableCell className="cont-col-sm" sx={{ color: 'white', fontWeight: 'bold' }} align="right">Haber</TableCell>
                    <TableCell className="cont-col-sm" sx={{ color: 'white', fontWeight: 'bold' }}>Mon.</TableCell>
                    <TableCell className="cont-col-md" sx={{ color: 'white', fontWeight: 'bold' }}>Doc. SUNAT / Medio Pago</TableCell>
                    <TableCell className="cont-col-md" sx={{ color: 'white', fontWeight: 'bold' }}>Estado</TableCell>
                    <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Acciones</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={10} align="center" sx={{ py: 4 }}>
                      <CircularProgress size={28} />
                    </TableCell>
                  </TableRow>
                ) : asientos.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={10} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                      {periodoAbierto
                        ? 'No hay asientos en este período. Use "Nuevo Asiento" para registrar el primero.'
                        : 'No hay asientos en este período.'}
                    </TableCell>
                  </TableRow>
                ) : asientos.map(a => (
                  <TableRow key={a.id} hover sx={{ opacity: a.estado === 'ANULADO' ? 0.55 : 1 }}>
                    <TableCell sx={{ fontFamily: 'monospace', fontWeight: 'bold', whiteSpace: 'nowrap' }}>
                      {a.tipo_documento}-{a.numero}
                    </TableCell>
                    <TableCell className="cont-col-sm">
                      <Tooltip title={a.tipo_desc || ''}>
                        <Chip label={a.tipo_documento} size="small" variant="outlined" />
                      </Tooltip>
                    </TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{a.fecha?.substring(0, 10)}</TableCell>
                    <TableCell className="cont-cell-truncate" sx={{ maxWidth: 260, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      <Tooltip title={a.glosa}><span>{a.glosa}</span></Tooltip>
                    </TableCell>
                    <TableCell className="cont-col-sm" align="right" sx={{ fontFamily: 'monospace' }}>{fmtNum(a.total_debe)}</TableCell>
                    <TableCell className="cont-col-sm" align="right" sx={{ fontFamily: 'monospace' }}>{fmtNum(a.total_haber)}</TableCell>
                    <TableCell className="cont-col-sm">
                      <Chip label={a.moneda || 'PEN'} size="small"
                        color={a.moneda === 'USD' ? 'info' : 'default'} variant="outlined" />
                    </TableCell>
                    <TableCell className="cont-col-md">
                      {a.origen === 'COBPAG' ? (
                        a.medio_pago ? (
                          <Tooltip title={`Medio de pago/cobro${a.pago_referencia ? ` — Ref. ${a.pago_referencia}` : ''}`}>
                            <Chip label={a.medio_pago} size="small" variant="outlined" color="success" />
                          </Tooltip>
                        ) : '—'
                      ) : a.doc_tipo_sunat ? (
                        <Tooltip title={TIPOS_DOC_SUNAT.find(t => t.code === a.doc_tipo_sunat)?.label || a.doc_tipo_sunat}>
                          <span>{DOC_SUNAT_CORTO[a.doc_tipo_sunat] || a.doc_tipo_sunat}</span>
                        </Tooltip>
                      ) : '—'}
                    </TableCell>
                    <TableCell className="cont-col-md">
                      <Chip
                        label={a.estado === 'ANULADO' ? 'Anulado' : 'Activo'}
                        color={a.estado === 'ANULADO' ? 'error' : 'success'}
                        size="small" variant="outlined"
                        sx={{ fontWeight: 'bold', fontSize: 11 }}
                      />
                    </TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      <Tooltip title="Ver detalle">
                        <IconButton size="small" onClick={() => verDetalle(a)}>
                          <VisibilityIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      {a.estado !== 'ANULADO' && periodoAbierto && (
                        <Tooltip title="Editar asiento">
                          <IconButton size="small" color="primary"
                            onClick={() => abrirEditar(a)}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      {a.estado !== 'ANULADO' && periodoAbierto && (
                        <Tooltip title="Anular asiento (cambio de estado)">
                          <IconButton size="small" color="warning"
                            onClick={() => anular(a.id, `${a.tipo_documento}-${a.numero}`)}>
                            <BlockIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      <Tooltip title="Eliminar asiento (borrado físico)">
                        <IconButton size="small" color="error"
                          onClick={() => setConfirmEliminar({ id: a.id, ref: `${a.tipo_documento}-${a.numero}` })}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}

      {/* ─── Dialog: Nuevo Asiento ─────────────────────────────────────────── */}
      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="lg" fullWidth
        PaperProps={{ sx: { minHeight: '70vh' } }}
        onKeyDown={e => {
          if (e.key === 'F4' && sugerencia) { e.preventDefault(); aplicarSugerencia(); }
          if (e.key === 'Enter' && e.ctrlKey && !saving && cuadrado) { e.preventDefault(); guardar(); }
        }}>
        <DialogTitle sx={{ bgcolor: editandoId ? 'secondary.main' : 'primary.main', color: 'white', fontWeight: 'bold' }}>
          {editandoId ? `Editar Asiento #${editandoId}` : 'Nuevo Asiento Contable'} — Período {periodoSel}
        </DialogTitle>
        <DialogContent dividers>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2.5, alignItems: 'flex-start' }}>
            <TextField select label="Tipo Documento *" value={form.tipo_documento} size="small"
              onChange={e => setForm(f => ({ ...f, tipo_documento: e.target.value }))}
              sx={{ minWidth: 220, flex: '1 1 220px' }}>
              {tiposDoc.filter(t => MANUALES_TIPOS.includes(t.codigo)).map(t => (
                <MenuItem key={t.codigo} value={t.codigo}>{t.codigo} — {t.descripcion}</MenuItem>
              ))}
              {tiposDoc.filter(t => CAJA_BANCOS_TIPOS.includes(t.codigo)).length > 0 && (
                <MenuItem disabled sx={{ fontSize: 11, color: '#0277bd', py: 0, fontWeight: 'bold' }}>─── Caja y Bancos ───</MenuItem>
              )}
              {tiposDoc.filter(t => CAJA_BANCOS_TIPOS.includes(t.codigo)).map(t => (
                <MenuItem key={t.codigo} value={t.codigo}>{t.codigo} — {t.descripcion}</MenuItem>
              ))}
              {tiposDoc.filter(t => !MANUALES_TIPOS.includes(t.codigo) && !CAJA_BANCOS_TIPOS.includes(t.codigo)).length > 0 && (
                <MenuItem disabled sx={{ fontSize: 11, color: '#aaa', py: 0 }}>─── ERP ───</MenuItem>
              )}
              {tiposDoc.filter(t => !MANUALES_TIPOS.includes(t.codigo) && !CAJA_BANCOS_TIPOS.includes(t.codigo)).map(t => (
                <MenuItem key={t.codigo} value={t.codigo}>{t.codigo} — {t.descripcion}</MenuItem>
              ))}
            </TextField>
            <TextField label="Fecha *" type="date" value={form.fecha} size="small"
              InputLabelProps={{ shrink: true }}
              onChange={e => setForm(f => ({ ...f, fecha: e.target.value }))}
              sx={{ minWidth: 155, flex: '0 0 155px' }} />
            <TextField select label="Moneda" value={form.moneda} size="small"
              onChange={e => setForm(f => ({
                ...f, moneda: e.target.value,
                // Al pasar de PEN a otra moneda sin que el usuario ya haya elegido un tipo de
                // TC, se preselecciona "TC Venta SBS" para que el auto-completado por fecha
                // dispare de inmediato (igual que en Registro de Compras/Ventas), sin forzar
                // una elección manual previa. Si ya había elegido algo, se respeta.
                tipo_tc: e.target.value === 'PEN' ? 'LIBRE' : (f.tipo_tc === 'LIBRE' ? 'VENTA_SBS' : f.tipo_tc),
              }))}
              sx={{ minWidth: 170, flex: '0 0 170px' }}>
              <MenuItem value="PEN">PEN — Soles</MenuItem>
              <MenuItem value="USD">USD — Dólares</MenuItem>
              <MenuItem value="EUR">EUR — Euros</MenuItem>
            </TextField>
            {form.moneda !== 'PEN' && (
              <TextField select label="Tipo TC" value={form.tipo_tc} size="small"
                onChange={e => setForm(f => ({ ...f, tipo_tc: e.target.value }))}
                sx={{ minWidth: 170, flex: '0 0 170px' }}>
                <MenuItem value="COMPRA_SBS">TC Compra SBS</MenuItem>
                <MenuItem value="VENTA_SBS">TC Venta SBS</MenuItem>
                <MenuItem value="LIBRE">TC Libre</MenuItem>
                <MenuItem value="SIN_CAMBIO">Sin Cambio (1.000)</MenuItem>
              </TextField>
            )}
            {form.moneda !== 'PEN' && (
              <TextField label="T. Cambio" value={form.tipo_cambio} size="small" type="number"
                inputProps={{ step: '0.001', min: 0 }}
                disabled={form.tipo_tc === 'SIN_CAMBIO'}
                onChange={e => setForm(f => ({ ...f, tipo_cambio: e.target.value }))}
                sx={{ minWidth: 110, flex: '0 0 110px' }} />
            )}
            <TextField label="Glosa (descripción del asiento) *" value={form.glosa} size="small"
              onChange={e => setForm(f => ({ ...f, glosa: e.target.value }))}
              placeholder="Ej: Por ventas del mes, pago proveedores..."
              sx={{ minWidth: 260, flex: '2 1 260px' }} />
          </Box>

          {/* ─── Referencia al comprobante que se cancela (Caja/Bancos) ─ */}
          {CAJA_BANCOS_TIPOS.includes(form.tipo_documento) && (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, mb: 2, alignItems: 'center',
              bgcolor: '#e3f2fd', borderRadius: 1, p: 1.5, border: '1px solid #90caf9' }}>
              <Typography variant="caption" fontWeight="bold" color="primary.dark"
                sx={{ width: '100%', mb: 0.5 }}>
                Comprobante que se cancela (referencia):
              </Typography>
              <TextField select label="Tipo" size="small" value={ple.doc_ref_tipo}
                onChange={e => setPle(p => ({ ...p, doc_ref_tipo: e.target.value }))}
                sx={{ minWidth: 200, flex: '1 1 200px' }}>
                <MenuItem value="">— Sin especificar —</MenuItem>
                {TIPOS_DOC_SUNAT.map(t => <MenuItem key={t.code} value={t.code}>{t.label}</MenuItem>)}
              </TextField>
              <TextField label="Serie" size="small" value={ple.doc_ref_serie}
                onChange={e => setPle(p => ({ ...p, doc_ref_serie: e.target.value.toUpperCase() }))}
                inputProps={{ maxLength: 10, style: { fontFamily: 'monospace' } }}
                placeholder="F001" sx={{ minWidth: 90, flex: '0 0 90px' }} />
              <TextField label="Número" size="small" value={ple.doc_ref_numero}
                onChange={e => setPle(p => ({ ...p, doc_ref_numero: e.target.value }))}
                inputProps={{ maxLength: 20, style: { fontFamily: 'monospace' } }}
                placeholder="00001234" sx={{ minWidth: 130, flex: '0 0 130px' }} />
            </Box>
          )}

          {/* ─── Datos del Comprobante (PLE) ──────────────────────────── */}
          <Box sx={{ mb: 1.5 }}>
            <Button size="small" variant="text" color="secondary"
              startIcon={pleOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              onClick={() => setPleOpen(v => !v)}
              sx={{ textTransform: 'none', fontWeight: 'bold', pl: 0 }}>
              Datos del Comprobante / PLE (opcional)
            </Button>
            <Collapse in={pleOpen}>
              <Paper variant="outlined" sx={{ p: 2, mt: 0.5, bgcolor: '#f9f9ff' }}>
                <Grid container spacing={1.5}>
                  <Grid item xs={12} sm={4}>
                    <TextField select label="Libro PLE" size="small" fullWidth value={ple.tipo_libro}
                      onChange={e => setPle(p => ({ ...p, tipo_libro: e.target.value }))}
                      sx={{ minWidth: 170 }}>
                      <MenuItem value=""><em>Sin especificar</em></MenuItem>
                      {TIPOS_LIBRO.map(t => <MenuItem key={t.code} value={t.code}>{t.label}</MenuItem>)}
                    </TextField>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField select label="Tipo Comp. SUNAT" size="small" fullWidth value={ple.doc_tipo_sunat}
                      onChange={e => setPle(p => ({ ...p, doc_tipo_sunat: e.target.value }))}
                      sx={{ minWidth: 170 }}>
                      <MenuItem value=""><em>Sin especificar</em></MenuItem>
                      {TIPOS_DOC_SUNAT.map(t => <MenuItem key={t.code} value={t.code}>{t.label}</MenuItem>)}
                    </TextField>
                  </Grid>
                  <Grid item xs={6} sm={2}>
                    <TextField label="Serie" size="small" fullWidth value={ple.doc_serie}
                      onChange={e => setPle(p => ({ ...p, doc_serie: e.target.value.toUpperCase() }))}
                      inputProps={{ maxLength: 10, style: { fontFamily: 'monospace' } }}
                      placeholder="F001" />
                  </Grid>
                  <Grid item xs={6} sm={2}>
                    <TextField label="Número" size="small" fullWidth value={ple.doc_numero}
                      onChange={e => setPle(p => ({ ...p, doc_numero: e.target.value }))}
                      inputProps={{ maxLength: 20, style: { fontFamily: 'monospace' } }}
                      placeholder="00001234" />
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <TextField label="Fecha Vencimiento" type="date" size="small" fullWidth
                      value={ple.doc_fecha_vencimiento} InputLabelProps={{ shrink: true }}
                      onChange={e => setPle(p => ({ ...p, doc_fecha_vencimiento: e.target.value }))} />
                  </Grid>
                  <Grid item xs={12} sm={5}>
                    <TextField label="Proyecto / Contrato (opcional)" size="small" fullWidth
                      value={ple.proyecto_nombre}
                      onChange={e => setPle(p => ({ ...p, proyecto_nombre: e.target.value }))}
                      inputProps={{ maxLength: 100 }} />
                  </Grid>
                  <Grid item xs={6} sm={2}>
                    <FormControlLabel control={
                      <Checkbox size="small" checked={!!ple.igv_especial}
                        onChange={e => setPle(p => ({ ...p, igv_especial: e.target.checked }))} />
                    } label={<Typography variant="caption">IGV 10.5%</Typography>} />
                  </Grid>
                  <Grid item xs={6} sm={2}>
                    <FormControlLabel control={
                      <Checkbox size="small" checked={!!ple.gasto_no_deducible}
                        onChange={e => setPle(p => ({ ...p, gasto_no_deducible: e.target.checked }))} />
                    } label={<Typography variant="caption">Gasto no deducible</Typography>} />
                  </Grid>

                  {/* Doc referencia (para NC/ND) */}
                  {(ple.doc_tipo_sunat === '07' || ple.doc_tipo_sunat === '08') && (
                    <>
                      <Grid item xs={12}><Divider><Typography variant="caption" color="text.secondary">Documento de Referencia</Typography></Divider></Grid>
                      <Grid item xs={12} sm={4}>
                        <TextField select label="Tipo Doc. Ref." size="small" fullWidth value={ple.doc_ref_tipo}
                          onChange={e => setPle(p => ({ ...p, doc_ref_tipo: e.target.value }))}
                          sx={{ minWidth: 170 }}>
                          <MenuItem value=""><em>-</em></MenuItem>
                          {TIPOS_DOC_SUNAT.map(t => <MenuItem key={t.code} value={t.code}>{t.label}</MenuItem>)}
                        </TextField>
                      </Grid>
                      <Grid item xs={6} sm={2}>
                        <TextField label="Serie Ref." size="small" fullWidth value={ple.doc_ref_serie}
                          onChange={e => setPle(p => ({ ...p, doc_ref_serie: e.target.value.toUpperCase() }))}
                          inputProps={{ maxLength: 10, style: { fontFamily: 'monospace' } }} />
                      </Grid>
                      <Grid item xs={6} sm={2}>
                        <TextField label="Número Ref." size="small" fullWidth value={ple.doc_ref_numero}
                          onChange={e => setPle(p => ({ ...p, doc_ref_numero: e.target.value }))}
                          inputProps={{ maxLength: 20, style: { fontFamily: 'monospace' } }} />
                      </Grid>
                    </>
                  )}

                  {/* Aduana (para DUA/DAM) */}
                  {(ple.doc_tipo_sunat === '50' || ple.doc_tipo_sunat === '52') && (
                    <>
                      <Grid item xs={12}><Divider><Typography variant="caption" color="text.secondary">Datos Aduana</Typography></Divider></Grid>
                      <Grid item xs={6} sm={3}>
                        <TextField label="Código Aduana" size="small" fullWidth value={ple.aduana_codigo}
                          onChange={e => setPle(p => ({ ...p, aduana_codigo: e.target.value }))}
                          inputProps={{ maxLength: 5 }} placeholder="0008" />
                      </Grid>
                    </>
                  )}

                  {/* Checkboxes detraccion/retencion/percepcion */}
                  <Grid item xs={12}>
                    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mt: 0.5 }}>
                      <FormControlLabel control={
                        <Checkbox size="small" checked={!!ple.tiene_detraccion}
                          onChange={e => setPle(p => ({ ...p, tiene_detraccion: e.target.checked }))} />
                      } label="Detracción SPOT" />
                      <FormControlLabel control={
                        <Checkbox size="small" checked={!!ple.tiene_retencion}
                          onChange={e => setPle(p => ({ ...p, tiene_retencion: e.target.checked }))} />
                      } label="Retención" />
                      <FormControlLabel control={
                        <Checkbox size="small" checked={!!ple.tiene_percepcion}
                          onChange={e => setPle(p => ({ ...p, tiene_percepcion: e.target.checked }))} />
                      } label="Percepción" />
                    </Box>
                  </Grid>

                  {/* Detraccion */}
                  {ple.tiene_detraccion && (
                    <>
                      <Grid item xs={12}><Divider sx={{ mt: 0 }}><Typography variant="caption" color="warning.main" fontWeight="bold">DETRACCIÓN SPOT</Typography></Divider></Grid>
                      <Grid item xs={6} sm={2}>
                        <TextField label="Fecha SPOT" type="date" size="small" fullWidth
                          value={ple.det_fecha} InputLabelProps={{ shrink: true }}
                          onChange={e => setPle(p => ({ ...p, det_fecha: e.target.value }))} />
                      </Grid>
                      <Grid item xs={6} sm={3}>
                        <TextField label="N° Constancia" size="small" fullWidth value={ple.det_numero}
                          onChange={e => setPle(p => ({ ...p, det_numero: e.target.value }))}
                          inputProps={{ maxLength: 20, style: { fontFamily: 'monospace' } }} />
                      </Grid>
                      <Grid item xs={4} sm={2}>
                        <TextField label="Tasa %" type="number" size="small" fullWidth value={ple.det_tasa}
                          onChange={e => setPle(p => ({ ...p, det_tasa: e.target.value }))}
                          inputProps={{ min: 0, max: 100, step: 0.01 }} />
                      </Grid>
                      <Grid item xs={4} sm={2}>
                        <TextField label="Monto S/" type="number" size="small" fullWidth value={ple.det_monto}
                          onChange={e => setPle(p => ({ ...p, det_monto: e.target.value }))}
                          inputProps={{ min: 0, step: 0.01 }} />
                      </Grid>
                      <Grid item xs={4} sm={3}>
                        <TextField label="Tipo SPOT (tabla 54)" size="small" fullWidth value={ple.det_tipo}
                          onChange={e => setPle(p => ({ ...p, det_tipo: e.target.value }))}
                          inputProps={{ maxLength: 3, style: { fontFamily: 'monospace' } }}
                          placeholder="022" />
                      </Grid>
                    </>
                  )}

                  {/* Retencion */}
                  {ple.tiene_retencion && (
                    <>
                      <Grid item xs={12}><Divider sx={{ mt: 0 }}><Typography variant="caption" color="error.main" fontWeight="bold">RETENCIÓN</Typography></Divider></Grid>
                      <Grid item xs={6} sm={3}>
                        <TextField label="Código Tasa Ret." size="small" fullWidth value={ple.ret_codigo_tasa}
                          onChange={e => setPle(p => ({ ...p, ret_codigo_tasa: e.target.value }))}
                          inputProps={{ maxLength: 3, style: { fontFamily: 'monospace' } }}
                          placeholder="01" helperText="01=3%, 02=6%" />
                      </Grid>
                      <Grid item xs={6} sm={3}>
                        <TextField label="N° Retención" size="small" fullWidth value={ple.ret_numero}
                          onChange={e => setPle(p => ({ ...p, ret_numero: e.target.value }))}
                          inputProps={{ maxLength: 20, style: { fontFamily: 'monospace' } }} />
                      </Grid>
                    </>
                  )}

                  {/* Percepcion */}
                  {ple.tiene_percepcion && (
                    <>
                      <Grid item xs={12}><Divider sx={{ mt: 0 }}><Typography variant="caption" color="info.main" fontWeight="bold">PERCEPCIÓN</Typography></Divider></Grid>
                      <Grid item xs={6} sm={3}>
                        <TextField label="Código Tasa Per." size="small" fullWidth value={ple.per_codigo_tasa}
                          onChange={e => setPle(p => ({ ...p, per_codigo_tasa: e.target.value }))}
                          inputProps={{ maxLength: 3, style: { fontFamily: 'monospace' } }}
                          placeholder="01" helperText="01=2%, 02=1%" />
                      </Grid>
                      <Grid item xs={6} sm={3}>
                        <TextField label="N° Percepción" size="small" fullWidth value={ple.per_numero}
                          onChange={e => setPle(p => ({ ...p, per_numero: e.target.value }))}
                          inputProps={{ maxLength: 20, style: { fontFamily: 'monospace' } }} />
                      </Grid>
                    </>
                  )}
                </Grid>
              </Paper>
            </Collapse>
          </Box>

          <Divider sx={{ mb: 1.5 }}>
            <Typography variant="caption" color="text.secondary" fontWeight="bold">DETALLE DEL ASIENTO</Typography>
          </Divider>

          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: 'grey.100' }}>
                  <TableCell sx={{ fontWeight: 'bold', width: '38%' }}>Cuenta Contable</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', width: '30%' }}>Glosa línea</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', width: '13%' }} align="right">Debe</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', width: '13%' }} align="right">Haber</TableCell>
                  <TableCell sx={{ width: '6%' }} />
                </TableRow>
              </TableHead>
              <TableBody>
                {lineas.map((linea, i) => (
                  <LineaAsiento
                    key={i}
                    linea={linea}
                    onUpdate={(field, val) => updateLinea(i, field, val)}
                    onRemove={() => quitarLinea(i)}
                    canRemove={lineas.length > 2}
                    empresaId={empresaId}
                    onCuentaSelected={cuenta => handleCuentaSelected(cuenta, i)}
                    urcs={urcs}
                    tipDoc={form.tipo_documento}
                    centrosCosto={centrosCosto}
                    usaCentroCosto={usaCentroCosto}
                  />
                ))}
                <TableRow sx={{
                  bgcolor: totalDebe === 0 ? 'grey.50' : cuadrado ? '#e8f5e9' : '#ffebee'
                }}>
                  <TableCell colSpan={2} align="right" sx={{ fontWeight: 'bold', pr: 2 }}>
                    TOTALES
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold', fontFamily: 'monospace', fontSize: 14 }}>
                    {totalDebe.toFixed(2)}
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold', fontFamily: 'monospace', fontSize: 14 }}>
                    {totalHaber.toFixed(2)}
                  </TableCell>
                  <TableCell />
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>

          {sugerencia && (
            <Alert severity="info" sx={{ mt: 1 }}
              action={
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button size="small" color="inherit" variant="outlined" startIcon={<AutoFixHighIcon />}
                    onClick={aplicarSugerencia}>
                    Aplicar
                  </Button>
                  <Button size="small" color="inherit" onClick={() => setSugerencia(null)}>
                    Ignorar
                  </Button>
                </Box>
              }>
              <strong>{sugerencia.cuentaObj.codigo}</strong> tiene{' '}
              {sugerencia.reglas.length > 0 && `${sugerencia.reglas.length} regla(s) automática(s)`}
              {sugerencia.reglas.length > 0 && sugerencia.destino && ' + '}
              {sugerencia.destino && `cuenta destino (${sugerencia.destino.codigo})`}.
              {' '}¿Agregar líneas compañeras? <strong style={{ opacity: 0.7 }}>(F4)</strong>
            </Alert>
          )}

          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mt: 1.5 }}>
            <Button size="small" variant="outlined" startIcon={<AddIcon />} onClick={nuevaLinea}>
              Agregar línea
            </Button>
            {totalDebe > 0 && !cuadrado && (
              <Alert severity="error" sx={{ py: 0, flex: 1 }} icon={false}>
                <strong>No cuadra.</strong> Diferencia: {diferencia.toFixed(2)}
              </Alert>
            )}
            {cuadrado && (
              <Alert severity="success" sx={{ py: 0, flex: 1 }} icon={false}>
                <strong>Asiento cuadrado.</strong> Total: {totalDebe.toFixed(2)}
              </Alert>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3 }}>
          <Button onClick={() => setOpen(false)} disabled={saving}>Cancelar</Button>
          <Button onClick={guardar} variant="contained" disabled={saving || !cuadrado}
            startIcon={saving ? <CircularProgress size={16} /> : null}>
            {saving ? 'Guardando...' : (editandoId ? 'Actualizar asiento' : 'Guardar asiento')}
            {!saving && <Typography component="span" sx={{ fontSize: 10, opacity: 0.65, ml: 0.8 }}>(Ctrl+Enter)</Typography>}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ─── Dialog: Ver Asiento ────────────────────────────────────────────── */}
      {verAsiento && (() => {
        const sumDebe = (verAsiento.detalles || []).reduce((s, d) => s + parseFloat(d.debe || 0), 0);
        const sumHaber = (verAsiento.detalles || []).reduce((s, d) => s + parseFloat(d.haber || 0), 0);
        const sumDebeMn = (verAsiento.detalles || []).reduce((s, d) => s + parseFloat(d.debe_mn || 0), 0);
        const sumHaberMn = (verAsiento.detalles || []).reduce((s, d) => s + parseFloat(d.haber_mn || 0), 0);
        const cuadra = Math.abs(sumDebe - sumHaber) < 0.02;
        // "esUsd" en el sentido de "moneda extranjera distinta a PEN" -- no está
        // hardcodeado a USD: debe_mn/haber_mn ya traen el equivalente en soles sin
        // importar la moneda de origen, así que esto funciona igual si algún día se
        // agrega otra moneda (hoy el sistema solo ofrece PEN/USD en los combos).
        const esUsd = !!verAsiento.moneda && verAsiento.moneda !== 'PEN';
        const difMn = +(sumDebeMn - sumHaberMn).toFixed(2);
        return (
        <Dialog open={!!verAsiento} onClose={() => setVerAsiento(null)} maxWidth="md" fullWidth>
          <DialogTitle sx={{
            display: 'flex', alignItems: 'center', gap: 1.5,
            bgcolor: verAsiento.estado === 'ANULADO' ? 'grey.500' : 'primary.main',
            color: 'white',
          }}>
            <Box component="span" sx={{
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
              bgcolor: 'rgba(255,255,255,0.18)', borderRadius: 1, px: 1, py: 0.3,
              fontFamily: 'monospace', fontWeight: 'bold', fontSize: 15,
            }}>
              {verAsiento.tipo_documento}-{verAsiento.numero}
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography variant="subtitle1" fontWeight="bold" lineHeight={1.2}>
                {verAsiento.tipo_desc || 'Asiento Contable'}
              </Typography>
              <Typography variant="caption" sx={{ opacity: 0.85 }}>
                {verAsiento.fecha?.substring(0, 10)} · Período {verAsiento.periodo}
              </Typography>
            </Box>
            {verAsiento.estado === 'ANULADO' ? (
              <Chip label="ANULADO" sx={{ bgcolor: 'white', color: 'grey.700', fontWeight: 'bold' }} size="small" />
            ) : (
              <Chip label={cuadra ? 'CUADRADO ✓' : 'DESCUADRADO'} size="small"
                sx={{ bgcolor: cuadra ? '#2e7d32' : '#c62828', color: 'white', fontWeight: 'bold' }} />
            )}
          </DialogTitle>
          <DialogContent dividers sx={{ bgcolor: 'grey.50' }}>
            {/* ── Fila 1: Glosa a todo el ancho ── */}
            <Paper variant="outlined" sx={{ p: 1.2, mb: 1.5 }}>
              <Typography variant="caption" color="text.secondary">GLOSA</Typography>
              <Typography fontWeight="medium" sx={{ wordBreak: 'break-word' }}>{verAsiento.glosa || '—'}</Typography>
            </Paper>

            {/* ── Fila 2: Moneda / T.Cambio / Debe / Haber (+ equivalente S/ si es USD) ── */}
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, mb: esUsd && Math.abs(difMn) > 0.001 ? 1 : 2 }}>
              <Paper variant="outlined" sx={{ flex: '1 1 110px', p: 1.2, textAlign: 'center' }}>
                <Typography variant="caption" color="text.secondary">MONEDA</Typography>
                <Typography fontWeight="bold" color={esUsd ? 'info.main' : 'text.primary'}>
                  {verAsiento.moneda || 'PEN'}
                </Typography>
              </Paper>
              <Paper variant="outlined" sx={{ flex: '1 1 110px', p: 1.2, textAlign: 'center' }}>
                <Typography variant="caption" color="text.secondary">T. CAMBIO</Typography>
                <Typography fontWeight="bold" fontFamily="monospace">{parseFloat(verAsiento.tipo_cambio || 1).toFixed(3)}</Typography>
              </Paper>
              <Paper variant="outlined" sx={{ flex: '1.4 1 160px', p: 1.2, textAlign: 'center', bgcolor: '#e8f5e9' }}>
                <Typography variant="caption" color="text.secondary">DEBE {esUsd ? '(US$)' : ''}</Typography>
                <Typography fontWeight="bold" fontFamily="monospace" color="#2e7d32">
                  {esUsd ? '$' : 'S/'} {sumDebe.toFixed(2)}
                </Typography>
              </Paper>
              <Paper variant="outlined" sx={{ flex: '1.4 1 160px', p: 1.2, textAlign: 'center', bgcolor: '#fff3e0' }}>
                <Typography variant="caption" color="text.secondary">HABER {esUsd ? '(US$)' : ''}</Typography>
                <Typography fontWeight="bold" fontFamily="monospace" color="#e65100">
                  {esUsd ? '$' : 'S/'} {sumHaber.toFixed(2)}
                </Typography>
              </Paper>
              {esUsd && (
                <>
                  <Paper variant="outlined" sx={{ flex: '1.4 1 160px', p: 1.2, textAlign: 'center', bgcolor: '#e3f2fd' }}>
                    <Typography variant="caption" color="text.secondary">DEBE (S/ equiv.)</Typography>
                    <Typography fontWeight="bold" fontFamily="monospace" color="#1565c0">S/ {sumDebeMn.toFixed(2)}</Typography>
                  </Paper>
                  <Paper variant="outlined" sx={{ flex: '1.4 1 160px', p: 1.2, textAlign: 'center', bgcolor: '#e3f2fd' }}>
                    <Typography variant="caption" color="text.secondary">HABER (S/ equiv.)</Typography>
                    <Typography fontWeight="bold" fontFamily="monospace" color="#1565c0">S/ {sumHaberMn.toFixed(2)}</Typography>
                  </Paper>
                </>
              )}
            </Box>
            {esUsd && Math.abs(difMn) > 0.001 && (
              <Alert severity="info" sx={{ mb: 2 }}>
                En soles hay una diferencia de S/ {difMn.toFixed(2)} por el redondeo al convertir cada línea con el tipo de cambio — el asiento sigue cuadrado en su moneda original ({verAsiento.moneda}).
              </Alert>
            )}

            {(verAsiento.doc_tipo_sunat || verAsiento.tipo_libro || verAsiento.tiene_detraccion || verAsiento.tiene_retencion || verAsiento.tiene_percepcion) && (
              <Paper variant="outlined" sx={{ mb: 2, borderLeft: '4px solid #1565c0', bgcolor: '#f0f6ff' }}>
                <Box sx={{ px: 1.5, py: 1 }}>
                  <Typography variant="caption" color="primary.main" fontWeight="bold" sx={{ letterSpacing: 0.5 }}>
                    DATOS SUNAT / PLE
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mt: 0.5, alignItems: 'center' }}>
                    {verAsiento.tipo_libro && (
                      <Box><Typography variant="caption" color="text.secondary">Libro PLE</Typography>
                        <Typography variant="body2" fontWeight="bold">{verAsiento.tipo_libro}</Typography></Box>
                    )}
                    {verAsiento.origen === 'COBPAG' ? (
                      <>
                        {verAsiento.medio_pago && (
                          <Box><Typography variant="caption" color="text.secondary">Medio de Pago</Typography>
                            <Typography variant="body2" fontWeight="bold">{verAsiento.medio_pago}</Typography></Box>
                        )}
                        {verAsiento.pago_referencia && (
                          <Box><Typography variant="caption" color="text.secondary">N° Operación</Typography>
                            <Typography variant="body2" fontFamily="monospace">{verAsiento.pago_referencia}</Typography></Box>
                        )}
                      </>
                    ) : verAsiento.doc_tipo_sunat && (
                      <Box><Typography variant="caption" color="text.secondary">Tipo Comp.</Typography>
                        <Typography variant="body2" fontWeight="bold">
                          {DOC_SUNAT_CORTO[verAsiento.doc_tipo_sunat] || verAsiento.doc_tipo_sunat}
                        </Typography></Box>
                    )}
                    {verAsiento.doc_serie && (
                      <Box><Typography variant="caption" color="text.secondary">Serie</Typography>
                        <Typography variant="body2" fontFamily="monospace">{verAsiento.doc_serie}</Typography></Box>
                    )}
                    {verAsiento.doc_numero && (
                      <Box><Typography variant="caption" color="text.secondary">Número</Typography>
                        <Typography variant="body2" fontFamily="monospace">{verAsiento.doc_numero}</Typography></Box>
                    )}
                    {(verAsiento.doc_ref_tipo || verAsiento.doc_ref_serie) && (
                      <Box><Typography variant="caption" color="text.secondary">Ref.</Typography>
                        <Typography variant="body2" fontFamily="monospace">
                          {verAsiento.doc_ref_tipo} {verAsiento.doc_ref_serie}-{verAsiento.doc_ref_numero}
                        </Typography></Box>
                    )}
                    {verAsiento.doc_fecha_vencimiento && (
                      <Box><Typography variant="caption" color="text.secondary">Vence</Typography>
                        <Typography variant="body2">{verAsiento.doc_fecha_vencimiento?.substring(0,10)}</Typography></Box>
                    )}
                    {!!verAsiento.tiene_detraccion && (
                      <Chip label={`Detracción ${verAsiento.det_tasa || ''}% S/${parseFloat(verAsiento.det_monto||0).toFixed(2)}`}
                        size="small" color="warning" sx={{ fontWeight: 'bold' }} />
                    )}
                    {!!verAsiento.tiene_retencion && (
                      <Chip label={`Retención ${verAsiento.ret_codigo_tasa || ''}`} size="small" color="error" sx={{ fontWeight: 'bold' }} />
                    )}
                    {!!verAsiento.tiene_percepcion && (
                      <Chip label={`Percepción ${verAsiento.per_codigo_tasa || ''}`} size="small" color="info" sx={{ fontWeight: 'bold' }} />
                    )}
                    {!!verAsiento.igv_especial && <Chip label="IGV 10.5%" size="small" color="secondary" sx={{ fontWeight: 'bold' }} />}
                    {!!verAsiento.gasto_no_deducible && <Chip label="No deducible" size="small" sx={{ fontWeight: 'bold' }} />}
                  </Box>
                </Box>
              </Paper>
            )}

            <TableContainer component={Paper} elevation={1}>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: 'primary.main' }}>
                    {[
                      'Línea', 'Código', 'Cuenta Contable', 'Glosa', 'URC',
                      esUsd ? 'Debe US$' : 'Debe', esUsd ? 'Haber US$' : 'Haber',
                      ...(esUsd ? ['Debe S/', 'Haber S/'] : []),
                    ].map((h, idx) => (
                      <TableCell key={`${h}-${idx}`} sx={{ fontWeight: 'bold', color: 'white' }}
                        align={['Línea'].includes(h) ? 'center' : (idx >= 5 ? 'right' : 'left')}>
                        {h}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(verAsiento.detalles || []).map((d, i) => (
                    <TableRow key={d.id} sx={{ bgcolor: i % 2 === 1 ? 'grey.50' : 'white' }}>
                      <TableCell align="center" sx={{ color: 'text.secondary' }}>{d.linea}</TableCell>
                      <TableCell>
                        <Chip label={d.cuenta_codigo} size="small" variant="outlined"
                          sx={{ fontFamily: 'monospace', fontWeight: 'bold', color: '#2e7d32', borderColor: '#2e7d32' }} />
                      </TableCell>
                      <TableCell>{d.cuenta_descripcion}</TableCell>
                      <TableCell sx={{ color: 'text.secondary', fontSize: 12 }}>{d.glosa_det || '—'}</TableCell>
                      <TableCell>
                        {d.urc_codigo ? (
                          <Chip label={d.urc_codigo} size="small" variant="outlined"
                            sx={{ fontFamily: 'monospace', fontSize: 10, height: 18,
                              color: d.urc_codigo <= '18' ? '#1565c0' : d.urc_codigo <= '28' ? '#2e7d32' : '#6a1b9a',
                              borderColor: 'currentColor' }} />
                        ) : <span style={{ color: '#ccc' }}>—</span>}
                      </TableCell>
                      <TableCell align="right" sx={{ fontFamily: 'monospace', fontWeight: parseFloat(d.debe || 0) > 0 ? 600 : 400, color: parseFloat(d.debe || 0) > 0 ? '#2e7d32' : 'text.disabled' }}>
                        {parseFloat(d.debe || 0) > 0 ? fmtNum(d.debe) : '—'}
                      </TableCell>
                      <TableCell align="right" sx={{ fontFamily: 'monospace', fontWeight: parseFloat(d.haber || 0) > 0 ? 600 : 400, color: parseFloat(d.haber || 0) > 0 ? '#e65100' : 'text.disabled' }}>
                        {parseFloat(d.haber || 0) > 0 ? fmtNum(d.haber) : '—'}
                      </TableCell>
                      {esUsd && (
                        <>
                          <TableCell align="right" sx={{ fontFamily: 'monospace', color: parseFloat(d.debe_mn || 0) > 0 ? '#1565c0' : 'text.disabled' }}>
                            {parseFloat(d.debe_mn || 0) > 0 ? fmtNum(d.debe_mn) : '—'}
                          </TableCell>
                          <TableCell align="right" sx={{ fontFamily: 'monospace', color: parseFloat(d.haber_mn || 0) > 0 ? '#1565c0' : 'text.disabled' }}>
                            {parseFloat(d.haber_mn || 0) > 0 ? fmtNum(d.haber_mn) : '—'}
                          </TableCell>
                        </>
                      )}
                    </TableRow>
                  ))}
                  <TableRow sx={{ bgcolor: cuadra ? '#e8f5e9' : '#ffebee', '& td': { fontWeight: 'bold', borderTop: '2px solid', borderColor: cuadra ? '#2e7d32' : '#c62828' } }}>
                    <TableCell colSpan={5} align="right">TOTAL</TableCell>
                    <TableCell align="right" sx={{ fontFamily: 'monospace', color: '#2e7d32' }}>
                      {sumDebe.toFixed(2)}
                    </TableCell>
                    <TableCell align="right" sx={{ fontFamily: 'monospace', color: '#e65100' }}>
                      {sumHaber.toFixed(2)}
                    </TableCell>
                    {esUsd && (
                      <>
                        <TableCell align="right" sx={{ fontFamily: 'monospace', color: '#1565c0' }}>
                          {sumDebeMn.toFixed(2)}
                        </TableCell>
                        <TableCell align="right" sx={{ fontFamily: 'monospace', color: '#1565c0' }}>
                          {sumHaberMn.toFixed(2)}
                        </TableCell>
                      </>
                    )}
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </DialogContent>
          <DialogActions sx={{ px: 3, py: 1.5, gap: 1 }}>
            <Button onClick={() => setVerAsiento(null)}>Cerrar</Button>
            <Box sx={{ flex: 1 }} />
            {verAsiento.estado !== 'ANULADO' && periodoAbierto && (
              <Button color="primary" variant="contained" startIcon={<EditIcon />}
                onClick={() => { setVerAsiento(null); abrirEditar(verAsiento); }}>
                Editar
              </Button>
            )}
            {verAsiento.estado !== 'ANULADO' && periodoAbierto && (
              <Button color="warning" variant="outlined" startIcon={<BlockIcon />}
                onClick={() => anular(verAsiento.id, `${verAsiento.tipo_documento}-${verAsiento.numero}`)}>
                Anular
              </Button>
            )}
            <Button color="error" variant="outlined" startIcon={<DeleteIcon />}
              onClick={() => { setVerAsiento(null); setConfirmEliminar({ id: verAsiento.id, ref: `${verAsiento.tipo_documento}-${verAsiento.numero}` }); }}>
              Eliminar
            </Button>
          </DialogActions>
        </Dialog>
        );
      })()}

      {/* ── Confirmación eliminar (borrado físico) ─────────────────────────── */}
      <Dialog open={!!confirmEliminar} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ color: 'error.main' }}>Eliminar asiento</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            ¿Está seguro de que desea <strong>eliminar definitivamente</strong> el asiento{' '}
            <strong>{confirmEliminar?.ref}</strong>?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Esta acción borrará permanentemente el asiento y todas sus líneas. No se puede deshacer.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmEliminar(null)}>Cancelar</Button>
          <Button variant="contained" color="error" onClick={eliminarAsiento}>
            Sí, eliminar definitivamente
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

// ─── Componente línea individual del asiento ────────────────────────────────

function LineaAsiento({ linea, onUpdate, onRemove, canRemove, empresaId, onCuentaSelected, urcs, tipDoc, centrosCosto, usaCentroCosto }) {
  const [opciones, setOpciones] = useState([]);
  const [inputVal, setInputVal] = useState(
    linea.cuentaObj ? `${linea.cuentaObj.codigo} — ${linea.cuentaObj.descripcion}` : ''
  );
  const [buscando, setBuscando] = useState(false);
  const timerRef = useRef(null);

  // Auxiliar autocomplete
  const [auxOpciones, setAuxOpciones] = useState([]);
  const [auxInputVal, setAuxInputVal] = useState(linea.auxiliar_ref || '');
  const [auxBuscando, setAuxBuscando] = useState(false);
  const auxTimerRef = useRef(null);

  // Popup documentos pendientes
  const [pendOpen, setPendOpen] = useState(false);
  const [pendRows, setPendRows] = useState([]);
  const [pendLoading, setPendLoading] = useState(false);

  const abrirPendientes = useCallback(async () => {
    if (!linea.auxiliar_id || !empresaId) return;
    const cod = linea.cuentaObj?.codigo || '';
    const tipo = cod.startsWith('42') ? '42' : '12';
    setPendLoading(true); setPendOpen(true); setPendRows([]);
    try {
      const r = await axios.get('/api/contabilidad/documentos-pendientes', {
        params: { empresa_id: empresaId, auxiliar_id: linea.auxiliar_id, tipo }
      });
      setPendRows(Array.isArray(r.data) ? r.data : []);
    } catch {}
    setPendLoading(false);
  }, [linea.auxiliar_id, linea.cuentaObj, empresaId]);

  // Registro rápido de auxiliar
  const [auxRegOpen, setAuxRegOpen] = useState(false);
  const [auxRegForm, setAuxRegForm] = useState({ numero_documento: '', descripcion: '', tipos_ids: [], direccion: '' });
  const [auxRegSaving, setAuxRegSaving] = useState(false);
  const [auxRegErr, setAuxRegErr] = useState('');
  const [auxRegRucLoading, setAuxRegRucLoading] = useState(false);
  const [auxTipos, setAuxTipos] = useState([]);

  useEffect(() => {
    setAuxInputVal(linea.auxiliar_ref || '');
  }, [linea.auxiliar_ref]);

  // Cargar tipos al abrir el mini-dialog
  const abrirAuxReg = useCallback(async (textoInicial) => {
    setAuxRegErr('');
    const esRuc = /^\d{11}$/.test((textoInicial || '').trim());
    const form = {
      numero_documento: esRuc ? textoInicial.trim() : '',
      descripcion: esRuc ? '' : (textoInicial || ''),
      tipos_ids: [], direccion: ''
    };
    setAuxRegForm(form);
    setAuxRegOpen(true);
    // Cargar tipos y pre-seleccionar PROVEEDOR por defecto
    if (empresaId) {
      try {
        const r = await axios.get('/api/contabilidad/auxiliar-tipos', { params: { empresa_id: empresaId } });
        setAuxTipos(r.data || []);
        if (r.data?.length > 0) {
          const prov = r.data.find(t => t.codigo === 'PROVEEDOR') || r.data[0];
          setAuxRegForm(p => ({ ...p, tipos_ids: [Number(prov.id)] }));
        }
      } catch {}
    }
    // Auto-consultar RUC si es 11 dígitos
    if (esRuc && empresaId) {
      setAuxRegRucLoading(true);
      try {
        const r = await axios.get('/api/contabilidad/auxiliares/consultar-ruc',
          { params: { ruc: textoInicial.trim(), empresa_id: empresaId } });
        setAuxRegForm(p => ({
          ...p,
          descripcion: r.data.razon_social || r.data.nombre || p.descripcion,
          direccion: r.data.direccion || r.data.direccion_completa || p.direccion || '',
        }));
      } catch {} finally { setAuxRegRucLoading(false); }
    }
  }, [empresaId]);

  const guardarAuxRapido = async () => {
    if (!auxRegForm.numero_documento.trim() || !auxRegForm.descripcion.trim()) {
      setAuxRegErr('N° documento y descripción son obligatorios'); return;
    }
    setAuxRegSaving(true); setAuxRegErr('');
    try {
      const tiposPrimario = auxTipos.find(t => Number(t.id) === auxRegForm.tipos_ids[0]);
      const r = await axios.post('/api/contabilidad/auxiliares', {
        empresa_id: empresaId,
        tipos_ids: auxRegForm.tipos_ids,
        tipo: tiposPrimario?.codigo || 'PROVEEDOR',
        numero_documento: auxRegForm.numero_documento.trim(),
        descripcion: auxRegForm.descripcion.trim(),
        direccion: auxRegForm.direccion || '',
        activo: 1
      });
      // Seleccionar el auxiliar recién creado
      const label = `${auxRegForm.numero_documento.trim()} — ${auxRegForm.descripcion.trim()}`;
      setAuxInputVal(label);
      onUpdate('auxiliar_ref', label);
      setAuxOpciones([]);
      setAuxRegOpen(false);
    } catch (e) {
      setAuxRegErr(e.response?.data?.error || 'Error al registrar');
    } finally { setAuxRegSaving(false); }
  };

  const buscarAuxiliar = useCallback((v) => {
    if (auxTimerRef.current) clearTimeout(auxTimerRef.current);
    auxTimerRef.current = setTimeout(async () => {
      if (!v || v.length < 2 || !empresaId) { setAuxOpciones([]); return; }
      setAuxBuscando(true);
      try {
        const r = await axios.get('/api/contabilidad/auxiliares/buscar', {
          params: { empresa_id: empresaId, q: v }
        });
        setAuxOpciones(Array.isArray(r.data) ? r.data : []);
      } catch {}
      setAuxBuscando(false);
    }, 280);
  }, [empresaId]);

  useEffect(() => {
    if (!linea.cuentaObj) setInputVal('');
  }, [linea.cuentaObj]);

  const buscar = useCallback((v) => {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(async () => {
      if (!v || v.length < 2 || !empresaId) { setOpciones([]); return; }
      setBuscando(true);
      try {
        const r = await axios.get('/api/contabilidad/plan', {
          params: { empresa_id: empresaId, solo_hojas: '1', buscar: v, limit: 30 }
        });
        setOpciones(Array.isArray(r.data) ? r.data : []);
      } catch {}
      setBuscando(false);
    }, 280);
  }, [empresaId]);

  return (
    <TableRow>
      <TableCell sx={{ py: 0.5, pr: 0.5 }}>
        <Autocomplete
          size="small"
          options={opciones}
          loading={buscando}
          filterOptions={x => x}
          getOptionLabel={o => o ? `${o.codigo} — ${o.descripcion}` : ''}
          value={linea.cuentaObj || null}
          inputValue={inputVal}
          onInputChange={(_, v, reason) => {
            setInputVal(v);
            if (reason === 'input') buscar(v);
            if (reason === 'clear') {
              onUpdate('cuenta_id', '');
              onUpdate('cuentaObj', null);
              setOpciones([]);
            }
          }}
          onChange={(_, v) => {
            onUpdate('cuenta_id', v?.id || '');
            onUpdate('cuentaObj', v || null);
            if (v) {
              setInputVal(`${v.codigo} — ${v.descripcion}`);
              if (onCuentaSelected) onCuentaSelected(v);
            }
          }}
          renderInput={p => (
            <TextField
              {...p}
              placeholder="Buscar cuenta (código o nombre)..."
              InputProps={{
                ...p.InputProps,
                endAdornment: (
                  <>
                    {buscando && <CircularProgress size={14} sx={{ mr: 1 }} />}
                    {p.InputProps.endAdornment}
                  </>
                )
              }}
            />
          )}
          renderOption={(props, o) => (
            <li {...props} key={o.id}>
              <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'baseline' }}>
                <Typography sx={{ fontFamily: 'monospace', fontWeight: 'bold', color: '#388e3c', minWidth: 70, fontSize: 12 }}>
                  {o.codigo}
                </Typography>
                <Typography variant="body2">{o.descripcion}</Typography>
              </Box>
            </li>
          )}
          isOptionEqualToValue={(o, v) => o.id === v?.id}
          noOptionsText={inputVal.length < 2 ? 'Escriba al menos 2 caracteres' : 'Sin resultados'}
          sx={{ minWidth: 0, width: '100%' }}
        />
      </TableCell>
      <TableCell sx={{ py: 0.5, px: 0.5 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          <TextField size="small" fullWidth placeholder="Glosa detalle"
            value={linea.glosa_det}
            onChange={e => onUpdate('glosa_det', e.target.value)} />
          {usaCentroCosto && (
            <TextField select size="small" fullWidth label="Centro de Costo"
              value={linea.centro_costo_id || ''}
              onChange={e => onUpdate('centro_costo_id', e.target.value)}>
              <MenuItem value="">— Sin asignar —</MenuItem>
              {(centrosCosto || []).map(c => (
                <MenuItem key={c.id} value={c.id}>{c.codigo} — {c.descripcion}</MenuItem>
              ))}
            </TextField>
          )}
          {linea.cuentaObj?.requiere_auxiliar === 1 && (
            <>
            <Autocomplete
              size="small"
              freeSolo
              options={(() => {
                // Sentinel option para forzar apertura del dropdown cuando no hay resultados
                if (auxInputVal.length >= 2 && !auxBuscando && auxOpciones.length === 0) {
                  return [{ _new: true, _query: auxInputVal }];
                }
                return auxOpciones;
              })()}
              loading={auxBuscando}
              filterOptions={x => x}
              getOptionLabel={o => {
                if (typeof o === 'string') return o;
                if (o._new) return '';
                return `${o.numero_documento || ''} — ${o.descripcion || ''}`;
              }}
              inputValue={auxInputVal}
              onInputChange={(_, v, reason) => {
                setAuxInputVal(v);
                if (reason === 'input') { buscarAuxiliar(v); onUpdate('auxiliar_ref', v); }
                if (reason === 'clear') { onUpdate('auxiliar_ref', ''); setAuxOpciones([]); }
              }}
              onChange={(_, v) => {
                if (!v) return;
                if (v._new) { abrirAuxReg(v._query); return; }
                if (typeof v === 'object') {
                  const label = `${v.numero_documento || ''} — ${v.descripcion || ''}`;
                  setAuxInputVal(label);
                  onUpdate('auxiliar_ref', label);
                  onUpdate('auxiliar_id', v.id || null);
                  onUpdate('auxiliar_nombre', label);
                }
              }}
              renderOption={(props, o) => {
                if (o._new) {
                  return (
                    <li {...props} key="_new" style={{ padding: '6px 12px' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                        <Typography variant="caption" color="text.secondary" sx={{ flex: 1 }}>
                          Sin resultados para "{o._query}"
                        </Typography>
                        <Button size="small" variant="contained" color="primary"
                          sx={{ fontSize: 10, py: 0.3, px: 1.2, whiteSpace: 'nowrap' }}
                          onMouseDown={e => { e.preventDefault(); e.stopPropagation(); abrirAuxReg(o._query); }}>
                          + Registrar nuevo
                        </Button>
                      </Box>
                    </li>
                  );
                }
                return (
                  <li {...props} key={o.id}>
                    <Box>
                      <Typography variant="caption" sx={{ fontFamily: 'monospace', fontWeight: 700, color: 'primary.main' }}>{o.numero_documento}</Typography>
                      <Typography variant="body2" sx={{ fontSize: 11 }}>{o.descripcion}</Typography>
                    </Box>
                  </li>
                );
              }}
              renderInput={params => (
                <TextField {...params} size="small" fullWidth
                  placeholder={`Auxiliar (${linea.cuentaObj?.auxiliar_tipo || 'RUC/Nombre'})`}
                  inputProps={{ ...params.inputProps, style: { fontSize: 11 } }}
                  sx={{ '& .MuiInputBase-root': { bgcolor: '#fff9c4' } }}
                  InputProps={{ ...params.InputProps,
                    endAdornment: <>{auxBuscando ? <CircularProgress size={12} /> : null}{params.InputProps.endAdornment}</>
                  }} />
              )}
              noOptionsText="Escriba RUC o nombre..."
              sx={{ width: '100%' }} />

            {/* Mini-dialog: Registro Rápido de Auxiliar */}
            <Dialog open={auxRegOpen} onClose={() => setAuxRegOpen(false)} maxWidth="xs" fullWidth
              onClick={e => e.stopPropagation()}>
              <DialogTitle sx={{ py: 1.5, fontSize: 15, fontWeight: 700 }}>
                Registrar Auxiliar Rápido
              </DialogTitle>
              <DialogContent>
                {auxRegErr && <Alert severity="error" sx={{ mb: 1.5 }}>{auxRegErr}</Alert>}
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, pt: 0.5 }}>
                  <Box>
                    <Typography variant="caption" color="text.secondary"
                      sx={{ fontWeight: 600, mb: 0.5, display: 'block' }}>
                      Tipos{' '}
                      <span style={{ fontWeight: 400, color: '#bbb' }}>(puede marcar varios)</span>
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                      {auxTipos.filter(t => t.activo).map(t => {
                        const checked = auxRegForm.tipos_ids.includes(Number(t.id));
                        return (
                          <Chip key={t.id} label={t.descripcion} size="small"
                            onClick={() => {
                              const tid = Number(t.id);
                              setAuxRegForm(p => ({
                                ...p,
                                tipos_ids: checked
                                  ? p.tipos_ids.filter(x => x !== tid)
                                  : [...p.tipos_ids, tid]
                              }));
                            }}
                            variant={checked ? 'filled' : 'outlined'}
                            sx={{
                              cursor: 'pointer', fontSize: 11, height: 26,
                              bgcolor: checked ? (t.color || '#607d8b') : 'transparent',
                              color: checked ? '#fff' : (t.color || '#607d8b'),
                              borderColor: t.color || '#607d8b',
                              fontWeight: checked ? 700 : 400,
                              '&:hover': { opacity: 0.85 },
                            }} />
                        );
                      })}
                    </Box>
                  </Box>
                  <TextField size="small" label="N° Documento (RUC/DNI)" fullWidth
                    value={auxRegForm.numero_documento}
                    onChange={e => setAuxRegForm(p => ({ ...p, numero_documento: e.target.value.replace(/\D/g, '') }))}
                    InputProps={{
                      endAdornment: auxRegForm.numero_documento.length === 11 ? (
                        <InputAdornment position="end">
                          <Tooltip title="Consultar SUNAT">
                            <IconButton size="small" disabled={auxRegRucLoading}
                              onClick={async () => {
                                setAuxRegRucLoading(true);
                                try {
                                  const r = await axios.get('/api/contabilidad/auxiliares/consultar-ruc',
                                    { params: { ruc: auxRegForm.numero_documento, empresa_id: empresaId } });
                                  setAuxRegForm(p => ({
                                    ...p,
                                    descripcion: r.data.razon_social || r.data.nombre || p.descripcion,
                                    direccion: r.data.direccion || r.data.direccion_completa || p.direccion || '',
                                  }));
                                } catch (e) {
                                  setAuxRegErr('No se pudo consultar SUNAT');
                                } finally { setAuxRegRucLoading(false); }
                              }}>
                              {auxRegRucLoading ? <CircularProgress size={14} /> : <PersonSearchIcon fontSize="small" />}
                            </IconButton>
                          </Tooltip>
                        </InputAdornment>
                      ) : null
                    }} />
                  <TextField size="small" label="Razón Social / Nombre" fullWidth
                    value={auxRegForm.descripcion}
                    onChange={e => setAuxRegForm(p => ({ ...p, descripcion: e.target.value }))}
                    InputProps={{ endAdornment: auxRegRucLoading ? <CircularProgress size={14} /> : null }} />
                  <TextField size="small" label="Dirección" fullWidth
                    value={auxRegForm.direccion || ''}
                    onChange={e => setAuxRegForm(p => ({ ...p, direccion: e.target.value }))}
                    placeholder="Se completa automáticamente desde SUNAT"
                    inputProps={{ style: { fontSize: 12 } }} />
                </Box>
              </DialogContent>
              <DialogActions sx={{ px: 2, pb: 1.5 }}>
                <Button onClick={() => setAuxRegOpen(false)} size="small">Cancelar</Button>
                <Button onClick={guardarAuxRapido} variant="contained" size="small" disabled={auxRegSaving}>
                  {auxRegSaving ? <CircularProgress size={16} /> : 'Guardar y seleccionar'}
                </Button>
              </DialogActions>
            </Dialog>
            </>
          )}

          {/* Campo Referencia: visible en cuentas 12x y 42x */}
          {(linea.cuentaObj?.codigo?.startsWith('12') || linea.cuentaObj?.codigo?.startsWith('42')) && (
            <>
              <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                <TextField
                  size="small" fullWidth
                  placeholder="Ref. doc. (ej: F001-0045)"
                  value={linea.referencia || ''}
                  onChange={e => onUpdate('referencia', e.target.value)}
                  inputProps={{ style: { fontSize: 11, fontFamily: 'monospace' } }}
                  sx={{ '& .MuiInputBase-root': {
                    bgcolor: linea.referencia ? '#f3e5f5' : 'transparent',
                  } }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <LinkIcon sx={{ fontSize: 14, color: linea.referencia ? '#7b1fa2' : '#bbb' }} />
                      </InputAdornment>
                    )
                  }}
                />
                <Tooltip title={linea.auxiliar_id ? 'Ver documentos pendientes' : 'Seleccione un auxiliar primero'}>
                  <span>
                    <IconButton size="small"
                      disabled={!linea.auxiliar_id}
                      onClick={abrirPendientes}
                      sx={{ color: linea.auxiliar_id ? '#7b1fa2' : '#ccc',
                        bgcolor: linea.auxiliar_id ? '#f3e5f5' : 'transparent',
                        border: '1px solid', borderColor: linea.auxiliar_id ? '#ce93d8' : '#e0e0e0',
                        borderRadius: 1, '&:hover': { bgcolor: '#e1bee7' } }}>
                      <SearchIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </span>
                </Tooltip>
              </Box>

              {/* Popup documentos pendientes */}
              <Dialog open={pendOpen} onClose={() => setPendOpen(false)} maxWidth="sm" fullWidth
                onClick={e => e.stopPropagation()}>
                <DialogTitle sx={{ py: 1.5, fontSize: 14, fontWeight: 700, bgcolor: '#7b1fa2', color: '#fff' }}>
                  Documentos pendientes — {linea.auxiliar_nombre?.split(' — ')[1] || linea.auxiliar_ref}
                  <Typography variant="caption" display="block" sx={{ color: '#e1bee7', fontWeight: 400 }}>
                    {linea.cuentaObj?.codigo?.startsWith('42') ? 'Cuentas por Pagar (42x)' : 'Cuentas por Cobrar (12x)'}
                    {' '}— haga clic para seleccionar
                  </Typography>
                </DialogTitle>
                <DialogContent sx={{ p: 0 }}>
                  {pendLoading && <Box sx={{ py: 4, textAlign: 'center' }}><CircularProgress size={28} /></Box>}
                  {!pendLoading && pendRows.length === 0 && (
                    <Box sx={{ py: 3, textAlign: 'center' }}>
                      <Typography variant="body2" color="text.secondary">
                        No hay documentos con saldo pendiente para este auxiliar.
                      </Typography>
                    </Box>
                  )}
                  {!pendLoading && pendRows.length > 0 && (
                    <Table size="small">
                      <TableHead>
                        <TableRow sx={{ bgcolor: '#f3e5f5' }}>
                          {['Documento','Fecha','Tipo','Total Orig.','Pagado','Saldo Pend.'].map(h => (
                            <TableCell key={h} sx={{ fontWeight: 'bold', fontSize: 11, py: 0.75 }}>{h}</TableCell>
                          ))}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {pendRows.map((row, i) => {
                          const orig = parseFloat(row.monto_original || 0);
                          const pag  = parseFloat(row.monto_pagado   || 0);
                          return (
                            <TableRow key={i} hover
                              onClick={() => { onUpdate('referencia', row.referencia); setPendOpen(false); }}
                              sx={{ cursor: 'pointer',
                                bgcolor: linea.referencia === row.referencia ? '#ede7f6' : 'inherit',
                                '&:hover': { bgcolor: '#f3e5f5' } }}>
                              <TableCell sx={{ fontFamily: 'monospace', fontWeight: 'bold', color: '#7b1fa2', fontSize: 12 }}>
                                {row.referencia}
                                {linea.referencia === row.referencia && (
                                  <Chip label="selec." size="small"
                                    sx={{ ml: 0.5, height: 16, fontSize: 9, bgcolor: '#7b1fa2', color: '#fff' }} />
                                )}
                              </TableCell>
                              <TableCell sx={{ fontSize: 11 }}>{String(row.fecha_orig || '').substring(0,10)}</TableCell>
                              <TableCell sx={{ fontSize: 11 }}>{row.tipo_documento || '—'}</TableCell>
                              <TableCell align="right" sx={{ fontSize: 11, fontFamily: 'monospace' }}>{orig.toFixed(2)}</TableCell>
                              <TableCell align="right" sx={{ fontSize: 11, fontFamily: 'monospace', color: '#388e3c' }}>{pag.toFixed(2)}</TableCell>
                              <TableCell align="right" sx={{ fontSize: 12, fontFamily: 'monospace', fontWeight: 'bold',
                                color: parseFloat(row.saldo_pendiente) > 0 ? '#d32f2f' : '#388e3c' }}>
                                {parseFloat(row.saldo_pendiente).toFixed(2)}
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  )}
                </DialogContent>
                <DialogActions sx={{ px: 2, py: 1 }}>
                  <Button onClick={() => setPendOpen(false)} size="small">Cerrar</Button>
                </DialogActions>
              </Dialog>
            </>
          )}

          {urcs && urcs.length > 0 && (
            <TextField select size="small" fullWidth
              value={linea.urc_codigo || ''}
              onChange={e => onUpdate('urc_codigo', e.target.value || '')}
              SelectProps={{ displayEmpty: true }}
              InputLabelProps={{ shrink: true }}
              inputProps={{ style: { fontSize: 11 } }}
              sx={{ '& .MuiInputBase-root': { bgcolor: linea.urc_codigo ? '#e3f2fd' : 'transparent', fontSize: 11 } }}>
              <MenuItem value="" sx={{ fontSize: 11, color: 'text.disabled' }}><em>Sin URC (flujo de efectivo)</em></MenuItem>
              {['OPE','INV','FIN'].map(tipo => [
                <MenuItem key={`hdr-${tipo}`} disabled sx={{ fontSize: 10, fontWeight: 'bold', color: tipo==='OPE'?'#1565c0':tipo==='INV'?'#2e7d32':'#6a1b9a', py: 0 }}>
                  — {tipo === 'OPE' ? 'OPERACIÓN' : tipo === 'INV' ? 'INVERSIÓN' : 'FINANCIAMIENTO'} —
                </MenuItem>,
                ...urcs.filter(u => u.tipo_actividad === tipo).map(u => (
                  <MenuItem key={u.codigo} value={u.codigo} sx={{ fontSize: 11 }}>
                    <span style={{ fontFamily: 'monospace', fontWeight: 'bold', marginRight: 6, color: tipo==='OPE'?'#1565c0':tipo==='INV'?'#2e7d32':'#6a1b9a' }}>{u.codigo}</span>
                    {u.descripcion.length > 55 ? u.descripcion.slice(0, 55) + '…' : u.descripcion}
                  </MenuItem>
                ))
              ])}
            </TextField>
          )}
        </Box>
      </TableCell>
      <TableCell sx={{ py: 0.5, px: 0.5 }}>
        <TextField
          size="small" type="number" placeholder="0.00"
          value={linea.debe}
          onChange={e => {
            onUpdate('debe', e.target.value);
            if (parseFloat(e.target.value) > 0) onUpdate('haber', '');
          }}
          inputProps={{ style: { textAlign: 'right' }, min: 0, step: '0.01' }}
          sx={{ width: 110 }}
        />
      </TableCell>
      <TableCell sx={{ py: 0.5, px: 0.5 }}>
        <TextField
          size="small" type="number" placeholder="0.00"
          value={linea.haber}
          onChange={e => {
            onUpdate('haber', e.target.value);
            if (parseFloat(e.target.value) > 0) onUpdate('debe', '');
          }}
          inputProps={{ style: { textAlign: 'right' }, min: 0, step: '0.01' }}
          sx={{ width: 110 }}
        />
      </TableCell>
      <TableCell sx={{ py: 0.5, pl: 0.5 }} align="center">
        {canRemove && (
          <IconButton size="small" color="error" onClick={onRemove}>
            <DeleteIcon fontSize="small" />
          </IconButton>
        )}
      </TableCell>
    </TableRow>
  );
}
